import { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useBusinessExpenseTypes } from "@/hooks/data";
import { useDebounce } from "@/hooks/useDebounce";

interface BusinessExpenseFilters {
  selectedOrganizations: string[];
  selectedProperties: string[];
  selectedType: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  page: number;
  pageSize: number;
  searchTerm: string;
}

export const useBusinessExpenseFilters = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>("all");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [page, setPage] = useState(1);
  const pageSize = 100;

  const { data: expenseTypes } = useBusinessExpenseTypes();

  const typeOptions = useMemo(() => {
    if (!expenseTypes) return [];
    return expenseTypes.map((type) => ({
      value: type.id,
      label: type.name,
    }));
  }, [expenseTypes]);

  // Fetch paginated data using RPC
  const {
    data: paginatedData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "business-expenses-paginated",
      debouncedSearchTerm,
      selectedOrganizations,
      selectedProperties,
      selectedType,
      startDate,
      endDate,
      page,
    ],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("search_business_expenses", {
        p_property_ids: selectedProperties.length > 0 ? selectedProperties : null,
        p_organization_ids: selectedOrganizations.length > 0 ? selectedOrganizations : null,
        p_expense_type_id: selectedType !== "all" ? selectedType : null,
        p_date_from: startDate ? startDate.toISOString().split("T")[0] : null,
        p_date_to: endDate ? endDate.toISOString().split("T")[0] : null,
        p_page: page,
        p_page_size: pageSize,
        p_order_by: "payment_date",
        p_order_direction: "desc",
        p_search_term: debouncedSearchTerm.trim() || null,
      });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch all filtered records for CSV export
  const fetchAllFilteredExpenses = async () => {
    const { data, error } = await supabase.rpc("search_business_expenses", {
      p_property_ids: selectedProperties.length > 0 ? selectedProperties : null,
      p_organization_ids: selectedOrganizations.length > 0 ? selectedOrganizations : null,
      p_expense_type_id: selectedType !== "all" ? selectedType : null,
      p_date_from: startDate ? startDate.toISOString().split("T")[0] : null,
      p_date_to: endDate ? endDate.toISOString().split("T")[0] : null,
      p_page: 1,
      p_page_size: 10000,
      p_order_by: "payment_date",
      p_order_direction: "desc",
      p_search_term: debouncedSearchTerm.trim() || null,
    });

    if (error) throw error;
    return data || [];
  };

  const filteredData = useMemo(() => {
    return paginatedData || [];
  }, [paginatedData]);

  const totalCount = useMemo(() => {
    if (filteredData.length > 0 && filteredData[0].total_count) {
      return Number(filteredData[0].total_count);
    }
    return 0;
  }, [filteredData]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const hasActiveFilters =
    searchTerm.trim() !== "" ||
    selectedOrganizations.length > 0 ||
    selectedProperties.length > 0 ||
    selectedType !== "all" ||
    startDate !== undefined ||
    endDate !== undefined;

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedOrganizations([]);
    setSelectedProperties([]);
    setSelectedType("all");
    setStartDate(undefined);
    setEndDate(undefined);
    setPage(1);
  };

  return {
    // Data
    filteredData,
    allData: filteredData, // For compatibility
    isLoading,
    error,
    refetch,
    totalCount,
    totalPages,

    // Filters
    searchTerm,
    setSearchTerm,
    selectedOrganizations,
    setSelectedOrganizations,
    selectedProperties,
    setSelectedProperties,
    selectedType,
    setSelectedType,
    startDate,
    setStartDate,
    endDate,
    setEndDate,

    // Pagination
    page,
    setPage,
    pageSize,

    // Utilities
    typeOptions,
    hasActiveFilters,
    clearFilters,
    fetchAllFilteredExpenses,
  };
};